import CurrentDateContext from '@/context/CurrentDateContext';
import useSpecies from '@/hooks/species/useSpecies';
import useKeyValueStorage from '@/hooks/storage/useKeyValueStorage';
import { getMonthName } from '@/hooks/useSeason';
import { getFromAPI } from '@/lib/api';
import {
	CalendarAttributes,
	StrapiAPIResponseArray,
	StrapiEntry,
} from '@/lib/apiTypes';
import { isIterable } from '@/lib/array';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import useSWR, { preload } from 'swr';

preload('/calendar', getFromAPI);

const useCalendarOnlyForContext = () => {
	const { currentDate } = useContext(CurrentDateContext);
	const { species } = useSpecies();

	// Store cached species in state
	const { value: cachedCalendar, setValue: setCachedCalendar } =
		useKeyValueStorage<
			StrapiAPIResponseArray<CalendarAttributes> | undefined
		>('calendar', undefined);

	// Fetch from API
	const {
		data: remoteCalendar,
		error,
		isLoading,
	} = useSWR<StrapiAPIResponseArray<CalendarAttributes>>(
		'/calendar',
		getFromAPI,
		{
			revalidateOnReconnect: true,
			revalidateIfStale: true,
			revalidateOnFocus: true,
			revalidateOnMount: true,
			errorRetryCount: 5,
			errorRetryInterval: 5000,
			fallbackData: cachedCalendar,
		},
	);

	// Update cache if remote data is different
	useEffect(() => {
		if (
			remoteCalendar &&
			JSON.stringify(remoteCalendar) !== JSON.stringify(cachedCalendar)
		) {
			setCachedCalendar(remoteCalendar);
		}
	}, [remoteCalendar, cachedCalendar, setCachedCalendar]);

	// Return calendar, sorted by date ascending
	const calendar = useMemo<typeof cachedCalendar>(() => {
		const baseCalendar =
			cachedCalendar !== undefined ? cachedCalendar : remoteCalendar;
		// Check if it exists and is iterable (not an error object)
		if (!baseCalendar?.data || !isIterable(baseCalendar.data))
			return undefined;

		return {
			...baseCalendar,
			data: baseCalendar.data.sort(
				(a, b) =>
					new Date(a.attributes.date).getTime() -
					new Date(b.attributes.date).getTime(),
			),
		};
	}, [cachedCalendar, remoteCalendar]);

	// Map with species ID as key and array of dates as value
	const speciesDatesMap = useMemo(() => {
		if (!calendar) return undefined;

		const map = new Map<number, Date[]>();
		calendar.data.forEach((entry) => {
			const speciesId = entry.attributes?.speciesEntry?.data?.id;
			if (speciesId) {
				const date = new Date(entry.attributes.date);
				if (map.has(speciesId)) {
					map.get(speciesId)?.push(date);
				} else {
					map.set(speciesId, [date]);
				}
			}
		});

		return map;
	}, [calendar]);

	// Get date from a species by id
	const getDatesFromSpecies = useCallback(
		(speciesId: number) => {
			if (!speciesDatesMap) return [];

			return speciesDatesMap?.get(speciesId) || [];
		},
		[speciesDatesMap],
	);

	// Is species ID in calendar?
	const getIsSpeciesInCalendar = useCallback(
		(speciesId: number) => {
			if (!speciesDatesMap) return false;

			return speciesDatesMap.has(speciesId);
		},
		[speciesDatesMap],
	);

	// Get the calendar grouped by month
	const monthlyCalendar = useMemo<MonthlyCalendarEntry[] | undefined>(() => {
		if (!calendar || !species) return undefined;

		// Group the calendar entries in an array of months and years with the corresponding entries
		const months: MonthlyCalendarEntry[] = [];
		calendar.data.forEach((entry) => {
			const date = new Date(entry.attributes.date);
			const month = date.getMonth();
			const year = date.getFullYear();
			const monthIndex = months.findIndex(
				(m) => m.month === month && m.year === year,
			);

			if (monthIndex === -1) {
				months.push({
					month,
					year,
					displayName: getMonthName(month),
					entries: [entry],
					dates: [date],
				});
			} else {
				months[monthIndex].entries.push(entry);
				months[monthIndex].dates.push(date);
			}
		});

		return months;
	}, [calendar, species]);

	// Get the species of the week, select the closest date (in past) to today
	const speciesOfTheWeekDate = useMemo(() => {
		if (!calendar) return undefined;

		// Get the species of the week, select the closest date (in past) to today
		const today = currentDate;
		const closestDate = calendar?.data.reduce((closest, entry) => {
			const date = new Date(entry.attributes.date);
			return date <= today && date >= closest ? date : closest;
		}, new Date(0));

		if (!closestDate) return undefined;

		return closestDate;
	}, [calendar, currentDate]);

	return {
		calendar,
		getDatesFromSpecies,
		getIsSpeciesInCalendar,
		speciesOfTheWeekDate,
		monthlyCalendar,
		isLoading,
		error,
	};
};

export type MonthlyCalendarEntry = {
	month: number;
	year: number;
	displayName: string;
	entries: StrapiEntry<CalendarAttributes>[];
	dates: Date[];
};

export default useCalendarOnlyForContext;
